import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createBlock as _createBlock, createElementVNode as _createElementVNode, createVNode as _createVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7db6d504"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  id: "timeline",
  class: "w-full h-full pointer-events-none"
}
const _hoisted_2 = { class: "relative w-full border-t-2 border-solid border-white" }
const _hoisted_3 = { class: "sm:hidden w-full h-full" }
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "dark-gradient absolute left-0 top-0 w-full h-full pointer-events-none" }, null, -1))
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "light-gradient absolute left-0 top-0 w-full h-full pointer-events-none" }, null, -1))

import { computed, reactive, onMounted, onUnmounted } from 'vue'
import { MJPSection, MJPSectionData } from '../magicjohnsonpark/types'
import { useEventBus } from '@vueuse/core'
import TimelineStop from '../general/TimelineStop.vue'
import { TimelineItem } from './types'
import useLanguage from '@/composables/useLanguage'
import PopOver from './PopOver.vue'
import TimelineRow from './TimelineRow.vue'
import { gsap } from 'gsap'
import { ScrollToPlugin } from '@/assets/js/ScrollToPlugin.min.js'
import BasicButton from '../general/BasicButton.vue'
import { ButtonVariant } from '../general/types'

interface Props {
  data: Array<TimelineItem>
  sectionData: MJPSectionData
  currentSection: MJPSection
  thisSection: MJPSection
  currentSectionHasLoaded: boolean
  active: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SiteTimeline',
  props: {
    data: null,
    sectionData: null,
    currentSection: null,
    thisSection: null,
    currentSectionHasLoaded: { type: Boolean },
    active: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  data: Array<TimelineItem>
  sectionData: MJPSectionData
  currentSection: MJPSection
  thisSection: MJPSection
  currentSectionHasLoaded: boolean
  active: boolean
}

gsap.registerPlugin(ScrollToPlugin)


const timelineText = useLanguage('timeline')
const interfaceText = useLanguage('interface')
const popoverClosedBus = useEventBus('popover-closed')
const sectionChangedBus = useEventBus('section-changed')
const hotspotClickedBus = useEventBus('hotspot-clicked')
const state = reactive({
  selectedStopIndex: -1,
  timelineStopRefs: {},
})
const keyCodes = {
  LeftArrow: 37,
  RightArrow: 39,
}

const focusCurrentStop = () => {
  const timelineStopLabel = document.querySelector(
    `[data-test="timeline-stop-label-${state.selectedStopIndex}"]`
  ) as HTMLElement
  if (timelineStopLabel) {
    timelineStopLabel.focus()
  }
}

popoverClosedBus.on(() => {
  focusCurrentStop()
  state.selectedStopIndex = -1
})

const timelineBounds = computed(() => {
  let date, year
  let bounds = { min: 9999, max: -9999 }
  for (let i = 0; i < props.data.length; i++) {
    date = new Date(props.data[i].date)
    year = date.getFullYear()
    bounds.min = Math.min(bounds.min, year)
    bounds.max = Math.max(bounds.max, year)
  }
  return bounds
})

const timelineStops = computed(() => {
  let date, year, percentage, stopPointsUp
  let lastYear = 0
  let lastStopWasUp = false
  const bounds = timelineBounds
  const timespan = bounds.value.max - bounds.value.min
  return props.data.map((item) => {
    date = new Date(item.date)
    year = date.getFullYear()
    if (year - lastYear < 3) {
      stopPointsUp = !lastStopWasUp
    } else {
      stopPointsUp = lastStopWasUp
    }
    percentage = ((year - bounds.value.min) / timespan) * 100
    lastYear = year
    lastStopWasUp = stopPointsUp
    return {
      ...item,
      ...{ percentage: percentage, year: year, pointsUp: stopPointsUp },
    }
  })
})

const scrollToIndex = (index) => {
  gsap.to('#mobile-timeline', {
    duration: 0.5,
    scrollTo: { y: `#row-${index}`, offsetY: 30 },
  })
}

const toggleStop = (index) => {
  if (state.selectedStopIndex == index) {
    scrollToIndex(state.selectedStopIndex)
    state.selectedStopIndex = -1
  } else {
    state.selectedStopIndex = index
    scrollToIndex(index)
  }
}

const handleClickStop = (index) => {
  toggleStop(index)
}

const handleToggleRowExpanded = (index) => {
  toggleStop(index)
}

const handleToggleContentExpanded = (index) => {
  scrollToIndex(index)
}

const handleClickNextBtn = (evt) => {
  if (props.currentSection == MJPSection.History) {
    sectionChangedBus.emit({
      section: MJPSection.IntentToHeal,
      pageNumber: -1,
      forward: true,
      lastSection: props.currentSection,
    })
  }
}

sectionChangedBus.on(() => {
  if (
    props.currentSection == props.thisSection &&
    props.currentSectionHasLoaded
  ) {
    state.selectedStopIndex = 0
  }
})

hotspotClickedBus.on((index) => {
  if (index == -1) {
    focusCurrentStop()
    state.selectedStopIndex = -1
  }
})

const handleKeyDown = (e) => {
  var keyCode = e.keyCode || e.which

  switch (keyCode) {
    // left, up arrow
    case keyCodes.LeftArrow:
      previousTimelineStop()
      break

    // right, down arrow
    case keyCodes.RightArrow:
      nextTimelineStop()
      break

    default:
      break
  }
}

const previousTimelineStop = () => {
  if (state.selectedStopIndex > 0) {
    state.selectedStopIndex--
  }
}

const nextTimelineStop = () => {
  if (state.selectedStopIndex < timelineStops.value.length - 1) {
    state.selectedStopIndex++
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown, false)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass(["absolute w-full px-[10%] bottom-[255px] hidden sm:block", {
        'pointer-events-auto': props.active,
      }])
    }, [
      _createElementVNode("div", _hoisted_2, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(timelineStops), (stop, index) => {
          return (_openBlock(), _createBlock(TimelineStop, {
            ref_for: true,
            ref: (el) => (state.timelineStopRefs[stop.date] = el),
            key: index,
            index: index,
            data: stop,
            class: _normalizeClass({
            'pointer-events-none': !props.active,
            'pointer-events-auto': props.active,
            'cursor-pointer': props.active,
          }),
            isVisible: props.active,
            isActive: state.selectedStopIndex == index,
            onLabelClicked: handleClickStop,
            "data-index": index,
            "data-test": `stop-${index}`
          }, null, 8, ["index", "data", "class", "isVisible", "isActive", "data-index", "data-test"]))
        }), 128)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(timelineStops), (stop, index) => {
          return (_openBlock(), _createBlock(PopOver, {
            key: index,
            index: index,
            source: 
            state.timelineStopRefs[stop.date]
              ? state.timelineStopRefs[stop.date].state.element
              : null
          ,
            data: stop.popover,
            dir: "mjp",
            text: _unref(timelineText)[stop.id],
            isVisible: 
            props.active &&
            props.thisSection == props.currentSection &&
            state.selectedStopIndex == index
          ,
            baseTabIndex: (index + 1) * 10,
            offsetX: -5,
            offsetY: stop.pointsUp ? 110 : 25,
            "data-test": `popover-${index}`
          }, null, 8, ["index", "source", "data", "text", "isVisible", "baseTabIndex", "offsetY", "data-test"]))
        }), 128))
      ])
    ], 2),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", {
        id: "mobile-timeline",
        class: _normalizeClass(["absolute left-0 top-20 bottom-[150px] py-24 w-full overflow-y-scroll overscroll-y-auto", {
          'pointer-events-none': !props.active,
          'pointer-events-auto': props.active,
        }])
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(timelineStops), (stop, index) => {
          return (_openBlock(), _createBlock(TimelineRow, {
            id: `row-${index}`,
            key: index,
            data: stop,
            index: index,
            isActive: 
            props.thisSection == props.currentSection &&
            index == state.selectedStopIndex
          ,
            isFirst: index == 0,
            isLast: index == _unref(timelineStops).length - 1,
            onToggleRowExpanded: handleToggleRowExpanded,
            onToggleContentExpanded: handleToggleContentExpanded,
            "data-index": index,
            "data-test": `row-${index}`
          }, null, 8, ["id", "data", "index", "isActive", "isFirst", "isLast", "data-index", "data-test"]))
        }), 128)),
        _createVNode(BasicButton, {
          id: "next-btn",
          class: "absolute mt-5 left-5 right-5",
          tabindex: props.active ? _unref(timelineStops).length * 10 + 10 : -1,
          text: _unref(interfaceText).next,
          variant: _unref(ButtonVariant).LightOutline,
          onClick: handleClickNextBtn
        }, null, 8, ["tabindex", "text", "variant"])
      ], 2),
      _hoisted_4,
      _hoisted_5
    ])
  ]))
}
}

})